import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AddOnTextInput, Button, ErrorMessage, IconButton, Modal, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useProcessRefundMutation } from "@/redux/apis/booking/endpoints/payment";
import { BookingPaymentLog } from "@/redux/slices/booking/types";
import { addToast, getCurrencySymbol } from "@/utils";
import { useSelectedBooking } from "../../../hooks/useSelectedBooking";

const schema = z.object({
  amount: z.coerce.number({ invalid_type_error: "This field is required." }).min(1),
  reason: z.string().min(1, "Reason is required"),
});

type FormData = z.infer<typeof schema>;

interface RefundPaymentModalProps {
  payment: BookingPaymentLog;
  isOpen: boolean;
  onClose: () => void;
}

export const RefundPaymentModal = ({ payment, isOpen, onClose }: RefundPaymentModalProps) => {
  const [processRefund, { isLoading }] = useProcessRefundMutation();

  const {
    id: bookingId,
    cost: { currency },
  } = useSelectedBooking();

  const {
    register,
    formState: { isDirty, isSubmitting, errors },
    handleSubmit,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: 0,
      reason: "",
    },
  });

  const onSubmit = async (data: FormData) =>
    processRefund({
      bookingId,
      paymentId: payment.id,
      amount: data.amount * 100,
      reason: data.reason,
    })
      .unwrap()
      .then(() => {
        addToast("success", "Payment refunded successfully");
        reset();
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));

  const handleClose = () => {
    reset();
    onClose();
  };

  const availableAmount = Number(payment.balance) / 100;

  const renderHeader = () => (
    <div className="flex items-center justify-between">
      <Typography variant="title">Refund Payment</Typography>
      <IconButton className="border-none p-2" iconName="close" isCustomIcon iconSize="md" variant="secondary" onClick={handleClose} />
    </div>
  );

  return (
    <Modal open={isOpen} onClose={handleClose} className="relative w-[90vw] max-w-lg md:w-[500px]">
      {renderHeader()}

      <div className="mt-3 space-y-4">
        <div className="flex items-center justify-between rounded-lg bg-neutral-surface-gray p-4">
          <Typography variant="action">Available:</Typography>
          <Typography variant="action">
            {getCurrencySymbol(currency)}
            {availableAmount.toFixed(2)}
          </Typography>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6 space-y-2">
            <Typography variant="paragraph" className="font-medium">
              Refund Amount
            </Typography>
            <div className="[&_[data-headlessui-state]]:flex-1">
              <AddOnTextInput
                startAddOn={`${currency} ${getCurrencySymbol(currency)}`}
                {...register("amount", { valueAsNumber: true })}
                type="number"
                step="0.01"
                min="0"
                max={availableAmount}
                placeholder="0"
                autoFocus
              />
            </div>
            {errors.amount && <ErrorMessage className="text-sm" errors={errors} name="amount" />}
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              You may refund up to the value available
            </Typography>
          </div>

          <div className="mb-4 space-y-2">
            <Typography variant="paragraph" className="font-medium">
              Reason for Refund
            </Typography>
            <TextInput placeholder="Enter reason for return" {...register("reason")} />
            {errors.reason && <ErrorMessage className="text-sm" errors={errors} name="reason" />}
          </div>

          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Note: For all refunds, there will be a <span className="font-semibold text-primary">2% + $0.30</span> fee (capped at a maximum
            of $4), which will be debited from your bank account. Refunds will remain pending until the payment is successfully processed.
          </Typography>

          <Button type="submit" variant="primary" disabled={isSubmitting || !isDirty || isLoading} className="mt-3 w-full" size="lg">
            Process Refund
          </Button>
        </form>
      </div>
    </Modal>
  );
};
